<template>
  <div class="debug-strategy-tests">
    <h3 class="text-lg font-medium text-gray-800 mb-4">Debug Strategy Tests</h3>

    <div class="grid grid-cols-1 gap-4 mb-6">
      <div class="bg-white rounded-lg p-4 border border-gray-200">
        <h4 class="font-medium text-gray-700 mb-3">Material Sheet</h4>
        <div class="flex items-center space-x-2 mb-2">
          <div>{{ material.width }} × {{ material.height }} mm</div>
        </div>
      </div>

      <div class="bg-white rounded-lg p-4 border border-gray-200">
        <h4 class="font-medium text-gray-700 mb-3">Parts</h4>
        <ul class="space-y-2">
          <li
            v-for="(part, index) in parts"
            :key="index"
            class="flex items-center justify-between"
          >
            <span
              >Part {{ index + 1 }}: {{ part.width }} × {{ part.height }} mm ({{
                part.quantity
              }}
              pieces)</span
            >
          </li>
        </ul>
      </div>
    </div>

    <div class="bg-white rounded-lg p-4 border border-gray-200 mb-6">
      <h4 class="font-medium text-gray-700 mb-3">Test Selection</h4>
      <div class="grid grid-cols-2 gap-3 mb-4">
        <button
          v-for="testId in allTests"
          :key="testId"
          @click="
            runTest(testId);
            trackButtonClick(`Debug Test ${testId}`, 'debug');
          "
          :class="[
            'px-3 py-2 rounded text-sm font-medium transition-colors',
            currentTest === testId
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
          ]"
        >
          Test {{ testId }}
        </button>
      </div>

      <div v-if="currentTest" class="border-t border-gray-200 pt-3 mt-3">
        <h5 class="font-medium text-gray-700 mb-2">
          Test {{ currentTest }} Description:
        </h5>
        <p class="text-gray-600 whitespace-pre-line">
          {{ getTestDescription(currentTest) }}
        </p>
      </div>
    </div>

    <div v-if="result" class="bg-white rounded-lg p-4 border border-gray-200">
      <h4 class="font-medium text-gray-700 mb-3">Test Result</h4>

      <div class="mb-4">
        <div class="flex justify-between mb-2">
          <span class="text-gray-600">Waste Percentage:</span>
          <span class="font-medium" :class="getWastePercentageClass()"
            >{{ (result.wastePercentage * 100).toFixed(2) }}%</span
          >
        </div>
        <div class="flex justify-between">
          <span class="text-gray-600">Strategy Used:</span>
          <span>{{ result.strategyName }}</span>
        </div>
      </div>

      <!-- Visualization of the cutting diagram -->
      <div class="border border-gray-300 rounded p-1 bg-gray-50">
        <div
          class="relative border border-gray-400 bg-white"
          :style="{
            width: `${Math.min(material.width * 4, 400)}px`,
            height: `${Math.min(material.height * 4, 280)}px`,
          }"
        >
          <div
            v-for="(part, i) in result.usedMaterials[0]?.children || []"
            :key="i"
            class="absolute border border-gray-400"
            :style="{
              left: `${(part.x || 0) * 4}px`,
              top: `${(part.y || 0) * 4}px`,
              width: `${part.width * 4}px`,
              height: `${part.height * 4}px`,
              backgroundColor: getPartColor(i, part),
            }"
          >
            <div
              class="flex items-center justify-center h-full text-xs text-gray-700"
            >
              {{ part.width }}×{{ part.height }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <DebugVisualization v-if="result" :result="result" :scale="3" />
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue';
  import type { IMaterial } from '~/types/Material';
  import type { ICutResult } from '~/types/CutResult';
  import { CompareDebugStrategy } from '~/composables/strategies/CompareDebugStrategy';
  import { generateAllPartCombinations } from '~/utils/generate-part-combinations';
  import { get } from '@nuxt/ui/runtime/utils/index.js';

  // Initialize analytics tracking
  const { trackButtonClick } = useAnalytics();

  // Define interface for parts with quantity
  interface DebugPartWithQuantity extends IMaterial {
    quantity: number;
  }

  // Define the material sheet
  const material = ref<IMaterial>({
    id: 'material-debug-1',
    width: 100,
    height: 70,
    children: [],
  });

  // Define the parts
  const parts = ref<DebugPartWithQuantity[]>([
    { id: 'part-1', width: 30, height: 20, quantity: 10 },
    { id: 'part-2', width: 25, height: 15, quantity: 20 },
    // { id: 'part-3', width: 15, height: 5, quantity: 100 },
  ]);

  // Current test ID
  const currentTest = ref<number | null>(null);
  const result = ref<ICutResult | null>(null);

  // All available test IDs
  const combinationsData = generateAllPartCombinations(parts.value);

  // Use the combination data for allTests
  const allTestCombinations = computed(() => {
    const tests = [];
    let testId = 1;

    combinationsData.forEach((sizeGroup) => {
      sizeGroup.combinations.forEach((combo) => {
        tests.push({
          id: testId++,
          description: combo.description,
          parts: combo.parts,
          partSelections: combo.partSelections,
          selectionSize: combo.selectionSize,
        });
      });
    });

    return tests;
  });

  // All available test IDs
  const allTests = computed(() =>
    allTestCombinations.value.map((test) => test.id)
  );

  // Get test description
  const getTestDescription = (testId: number): string => {
    const test = allTestCombinations.value.find((test) => test.id === testId);
    return test ? test.description : 'No description available';
  };

  // Expand parts to proper IMaterial array
  const expandParts = () => {
    const expandedParts: IMaterial[] = [];

    parts.value.forEach((part, idx) => {
      for (let i = 0; i < part.quantity; i++) {
        expandedParts.push({
          id: `part-${idx + 1}-${i}`,
          width: part.width,
          height: part.height,
        });
      }
    });

    return expandedParts;
  };

  // Run a specific test
  const runTest = async (testId: number) => {
    currentTest.value = testId;

    // Create strategy instance
    const strategy = new CompareDebugStrategy();

    // Get the specific test configuration
    const testConfig = allTestCombinations.value.find(
      (test) => test.id === testId
    );

    if (!testConfig) return;

    // Use the parts from the test configuration
    const testParts = testConfig.parts.map((part) => ({
      id: part.id,
      width: part.width,
      height: part.height,
    }));
    const expandedParts = expandParts();

    // Create a clean material copy
    const materialCopy = { ...material.value, children: [] };

    // Run the test using the real strategy
    // You might need to adjust this depending on how your strategy works
    const testResult = strategy.runTest(
      testId,
      materialCopy,
      expandedParts,
      parts.value,
      allTestCombinations.value
    );
    result.value = testResult.result;
  };

  // Get color for a part based on its index
  const getPartColor = (index: number, part: IMaterial) => {
    const colors = [
      '#FFADAD',
      '#FFD6A5',
      '#FDFFB6',
      '#CAFFBF',
      '#9BF6FF',
      '#A0C4FF',
      '#BDB2FF',
      '#FFC6FF',
    ];

    // Determine which original part this is based on dimensions
    let partType = 0;
    for (let i = 0; i < parts.value.length; i++) {
      const p = parts.value[i];
      // Check if dimensions match in either orientation
      if (
        (p.width === part.width && p.height === part.height) ||
        (p.width === part.height && p.height === part.width)
      ) {
        partType = i + 1;
        break;
      }
    }

    return colors[(partType - 1) % colors.length] || '#CCCCCC';
  };

  // Get CSS class for waste percentage display
  const getWastePercentageClass = () => {
    if (!result.value) return '';

    const wastePercent = result.value.wastePercentage * 100;
    if (wastePercent < 10) return 'text-green-600';
    if (wastePercent < 25) return 'text-yellow-600';
    return 'text-red-600';
  };

  onMounted(() => {
    // Optionally run a default test on mount
    // runTest(1);
  });
</script>

<style scoped>
  .debug-strategy-tests {
    margin-bottom: 1.5rem;
  }
</style>
